import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import { CheckCircle } from '@phosphor-icons/react/dist/icons/CheckCircle';
import { Sparkle } from '@phosphor-icons/react/dist/icons/Sparkle';
import { Warning } from '@phosphor-icons/react/dist/icons/Warning';
import type { useCreativeCheck } from '../api-client';
import type { CreativeCheckResponse } from '../types';

type Suggestion = NonNullable<CreativeCheckResponse['suggestion']>;

function AspectRatioLine({ ratio }: { ratio: NonNullable<CreativeCheckResponse['aspectRatio']> }) {
  return ratio.ok ? (
    <span className="flex items-center gap-1.5 text-sm">
      <CheckCircle size={16} weight="fill" className="text-content-success" />
      {ratio.actual} fits ({ratio.accepted.join(' or ')})
    </span>
  ) : (
    <span className="flex items-center gap-1.5 text-sm font-medium text-text-red">
      <Warning size={16} weight="fill" />
      This is {ratio.actual}. It needs to be {ratio.accepted.join(' or ')}.
    </span>
  );
}

export function CreativeCheckCard({
  check,
  onApply,
}: {
  check: ReturnType<typeof useCreativeCheck>;
  onApply: (suggestion: Suggestion) => void;
}) {
  if (check.isIdle) return null;
  const report = check.data;
  const suggestion = report?.suggestion;

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-border-primary p-3.5">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Sparkle size={16} weight="fill" />
        Creative check
      </div>
      {check.isPending && (
        <Text size="sm" variant="tertiary">
          Looking at your image…
        </Text>
      )}
      {check.isError && (
        <Text size="sm" className="text-text-red">
          {check.error.message}
        </Text>
      )}
      {report && (
        <>
          {report.aspectRatio && <AspectRatioLine ratio={report.aspectRatio} />}
          <Text size="sm">{report.summary}</Text>
          {report.issues.length > 0 && (
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
              {report.issues.map(issue => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}
          {suggestion && (
            <div className="flex flex-col gap-2 rounded-lg bg-bg-secondary p-3 text-sm">
              <span className="font-medium">Suggested copy</span>
              {suggestion.headline && (
                <span>
                  <span className="text-content-tertiary">Headline: </span>
                  {suggestion.headline}
                </span>
              )}
              {suggestion.primaryText && (
                <span>
                  <span className="text-content-tertiary">Primary text: </span>
                  {suggestion.primaryText}
                </span>
              )}
              {suggestion.description && (
                <span>
                  <span className="text-content-tertiary">Description: </span>
                  {suggestion.description}
                </span>
              )}
              <div>
                <Button variant="secondary" size="sm" onClick={() => onApply(suggestion)}>
                  Use suggested copy
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
